import React, { useState } from "react";
import { useEffect } from "react";
import {
  MdEdit,
  MdSettings,
  MdContentCopy,
  MdStore,
  MdReceipt,
  MdAttachMoney,
  MdArrowForward,
  MdStar,
} from "react-icons/md";
import { useParams } from "react-router-dom";
import { getBookings, updateBookingStatus } from "../../services/booking.service";
import { t } from "i18next";
import { toast } from "react-toastify";

// Modal component
function Modal({ open, onClose, title, children, actions }) {
  if (!open) return null;
  return (
    <div className='fixed z-40 inset-0 bg-black bg-opacity-40 flex items-center justify-center backdrop-blur-sm'>
      <div className='bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden'>
        <div className='px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white'>
          <div className='flex items-center justify-between'>
            <h2 className='text-xl font-bold text-gray-800'>{title}</h2>
            <button
              onClick={onClose}
              className='text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors'
            >
              &times;
            </button>
          </div>
        </div>
        <div className='px-6 py-4 bg-white'>{children}</div>
        {actions && (
          <div className='px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 rounded-b-xl'>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export default function OnlineServiceOrdersPage() {
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [copied, setCopied] = useState(false);
  const [orders, setOrders] = useState([]);
  const { bussinessUuid } = useParams();
  const [updatingId, setUpdatingId] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // Store Settings dialog state
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const allOrders = await getBookings(bussinessUuid);
        setOrders(allOrders);
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      }
    };

    fetchOrders();
  }, [bussinessUuid]);

  const handleOpenInvoiceDialog = (order) => {
    setSelectedOrder(order);
    setInvoiceDialogOpen(true);
  };

  const handleVisitOnlineServices = () => {
    const storeUrl = `${window.location.origin}/store/${bussinessUuid}/?tab=services`;
    window.open(storeUrl, '_blank');
  };

  const handleCopyStoreLink = async () => {
    const storeUrl = `${window.location.origin}/store/${bussinessUuid}/`;
    try {
      await navigator.clipboard.writeText(storeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  const handleCloseInvoiceDialog = () => {
    setInvoiceDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleStatusChange = async (bookingId, status) => {
    if (!bussinessUuid || !bookingId) return;
    setUpdatingId(bookingId);
    try {
      const response = await updateBookingStatus(bussinessUuid, bookingId, status);
      const updatedBooking = response?.booking;
      setOrders((prev) =>
        prev.map((item) =>
          item._id === bookingId
            ? {
                ...item,
                ...(updatedBooking || {}),
                status: updatedBooking?.status || status,
              }
            : item
        )
      );

      if (status === "Confirmed") {
        if (response?.taskAssigned || response?.assignedTaskId) {
          toast.success("Booking confirmed and task assigned to employee.");
        } else {
          toast.success("Booking confirmed.");
        }
      } else if (status === "Cancelled") {
        toast.success("Booking cancelled and customer notified.");
      } else {
        toast.success("Booking status updated.");
      }
    } catch (error) {
      console.error("Failed to update booking status", error);
      toast.error(error?.response?.data?.message || "Failed to update booking status");
    } finally {
      setUpdatingId(null);
    }
  };

  const requestStatusChange = (booking, status) => {
    if (["Confirmed", "Cancelled"].includes(status)) {
      setPendingStatusChange({ booking, status });
      return;
    }

    handleStatusChange(booking._id, status);
  };

  const closeStatusConfirmModal = () => {
    setPendingStatusChange(null);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange?.booking?._id || !pendingStatusChange?.status) return;
    await handleStatusChange(pendingStatusChange.booking._id, pendingStatusChange.status);
    closeStatusConfirmModal();
  };

  function handleCreateInvoice() {
    alert("Invoice created!");
    handleCloseInvoiceDialog();
  }

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusColors = {
      Shipped: "bg-blue-100 text-blue-800",
      Pending: "bg-yellow-100 text-yellow-800",
      "In Progress": "bg-indigo-100 text-indigo-800",
      Delivered: "bg-green-100 text-green-800",
      Processing: "bg-purple-100 text-purple-800",
      Completed: "bg-blue-100 text-blue-800",
      Cancelled: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          statusColors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 py-8 px-4 sm:px-6 lg:px-8'>
      <div className='mx-auto max-w-7xl'>
      {/* DASHBOARD HEADER */}
      <div className='mb-8'>
        <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8'>
          <div>
            <h1 className='text-3xl font-bold bg-indigo-600 bg-clip-text text-transparent flex items-center gap-3'>
              <MdStore className='text-blue-600' size={28} />
              {t("ONLINE_SERVICE.SERVICE_DASHBOARD")}
            </h1>
            <p className='mt-2 text-gray-600 dark:text-gray-300'>
              {t("ONLINE_SERVICE.MANAGE_YOUR_SERVICES")}
            </p>
          </div>

          <div className='flex flex-col sm:flex-row gap-3'>
            <button
              onClick={handleVisitOnlineServices}
              className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-green-600 to-emerald-500 text-white hover:from-green-700 hover:to-emerald-600 transition-all shadow-md hover:shadow-lg transform hover:scale-105'


            >
              <MdStore size={20} />
              {t("ONLINE_SERVICE.VISIT_ONLINE_SERVICES")}
              <MdArrowForward size={18} />
            </button>

            <button
              onClick={handleCopyStoreLink}
              type='button'
              className={`inline-flex items-center justify-center gap-2 border px-5 py-3 rounded-lg font-medium transition-all ${
                copied
                  ? "border-green-500 text-green-700 bg-green-50 shadow-inner"
                  : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50 shadow-sm hover:shadow-md"
              }`}
              aria-label='Copy store link'
            >
              <MdContentCopy size={20} />
              {copied ? t("ONLINE_SERVICE.COPIED") : t("ONLINE_SERVICE.COPY_LINK")}
            </button>








          </div>
        </div>

        {/* STATS CARDS */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <div
            className='bg-white dark:bg-slate-800
                      p-6 rounded-2xl
                      shadow-sm dark:shadow-lg
                      border border-gray-100 dark:border-slate-700
                      transition-all duration-300 hover:shadow-lg hover:-translate-y-1'
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'>
                  {t("ONLINE_SERVICE.TOTAL_ORDERS")}
                </p>

                <p className='text-4xl font-bold text-gray-900 dark:text-white mt-2'>
                  {orders.length}
                </p>
              </div>

              <div
                className='p-4 rounded-2xl
                          bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/40 dark:to-blue-800/30
                          text-blue-600 dark:text-blue-400'
              >
                <MdReceipt size={28} />
              </div>
            </div>
          </div>


                    <div
            className='bg-white dark:bg-slate-800
                      p-6 rounded-2xl
                      shadow-sm dark:shadow-lg
                      border border-gray-100 dark:border-slate-700
                      transition-all duration-300 hover:shadow-lg hover:-translate-y-1'
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'>
                  {t("ONLINE_SERVICE.REVENUE")}
                </p>

                <p className='text-4xl font-bold text-gray-900 dark:text-white mt-2'>
                  ₹{orders

                    .reduce((sum, order) => sum + order.amount, 0)
                    .toFixed(2)}
                </p>
              </div>

              <div
                className='p-4 rounded-2xl
                          bg-gradient-to-br from-green-100 to-emerald-50 dark:from-green-900/40 dark:to-emerald-800/30
                          text-green-600 dark:text-green-400'
              >
                <MdAttachMoney size={28} />
              </div>
            </div>
          </div>


                    <div
            className='bg-white dark:bg-slate-800
                      p-6 rounded-2xl
                      shadow-sm dark:shadow-lg
                      border border-gray-100 dark:border-slate-700
                      transition-all duration-300 hover:shadow-lg hover:-translate-y-1'
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'>
                  {t("ONLINE_SERVICE.AVG_ORDER_VALUE")}
                </p>

                <p className='text-4xl font-bold text-gray-900 dark:text-white mt-2'>
                  ₹{orders.length > 0

                    ? (
                        orders.reduce((sum, order) => sum + order.amount, 0) /
                        orders.length
                      ).toFixed(2)
                    : "0.00"}
                </p>
              </div>

              <div
                className='p-4 rounded-2xl
                          bg-gradient-to-br from-purple-100 to-pink-50 dark:from-purple-900/40 dark:to-pink-800/30
                          text-purple-600 dark:text-purple-400'
              >
                <MdEdit size={28} />
              </div>
            </div>
          </div>
        </div>

        <div className='bg-white dark:bg-slate-800 shadow-lg rounded-2xl overflow-hidden border border-gray-100 dark:border-slate-700'>
            <div className='px-6 py-5 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 flex justify-between items-center'>
              <h2 className='text-lg font-bold text-gray-900 dark:text-white'>


                {t("ONLINE_SERVICE.RECENT_BOOKINGS")}
              </h2>
              <span className='inline-block px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-100 dark:bg-blue-900/40 dark:text-blue-300 rounded-full'>
                {orders.length} {orders.length === 1 ? 'booking' : 'bookings'}
              </span>
            </div>

            {/* Table with Vertical Scroll */}
            <div className='overflow-x-auto'>
              <div className='max-h-[500px] overflow-y-auto'>
                <table className='min-w-full divide-y divide-gray-100 dark:divide-slate-700'>
                  <thead className='bg-gray-50 dark:bg-slate-800/50 sticky top-0 z-10 border-b-2 border-gray-200 dark:border-slate-700'>
                    <tr>
                      <th className='py-4 px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider'>
                        {t("COMMON.SERVICE")}
                      </th>
                      <th className='py-4 px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider'>
                        {t("COMMON.CUSTOMER")}
                      </th>
                      <th className='py-4 px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider'>
                        {t("SIDER.DATE")}
                      </th>
                      <th className='py-4 px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider'>
                        {t("SIDER.TIME")}
                      </th>
                      <th className='py-4 px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider'>
                        {t("CARD.PAYMENT")}
                      </th>
                      <th className='py-4 px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider'>
                        Status
                      </th>
                      <th className='py-4 px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider'>
                        Review
                      </th>
                      <th className='py-4 px-6 text-right text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider'>
                        {t("SIDER.AMOUNT")} (₹)
                      </th>
                    </tr>
                  </thead>

                  <tbody className='bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-700'>
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan={8} className='py-12 px-6 text-center'>
                          <div className='flex flex-col items-center justify-center'>
                            <div className='p-4 rounded-full bg-gray-100 dark:bg-slate-800 mb-4'>
                              <MdReceipt size={32} className='text-gray-400 dark:text-slate-600' />
                            </div>
                            <p className='text-lg font-semibold text-gray-600 dark:text-gray-300'>
                              {t("ONLINE_SERVICE.NO_BOOKINGS_YET")}
                            </p>
                            <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                              {t("ONLINE_SERVICE.YOUR_SERVICE_BOOKING_WILL_APPEAR_HERE")}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      orders.map((booking) => (
                        <tr
                          key={booking._id}
                          className='hover:bg-blue-50 dark:hover:bg-slate-800/50 transition-colors'
                        >
                          <td className='py-4 px-6 whitespace-nowrap font-semibold text-gray-900 dark:text-white text-sm'>
                            {booking.serviceName}
                          </td>

                          <td className='py-4 px-6 whitespace-nowrap'>
                            <div className='flex items-center'>
                              <div className='flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 rounded-full flex items-center justify-center text-blue-700 dark:text-blue-400 font-bold text-sm'>
                                {booking.name?.charAt(0)?.toUpperCase()}
                              </div>
                              <div className='ml-3'>
                                <div className='text-sm font-semibold text-gray-900 dark:text-white'>
                                  {booking.name}
                                </div>
                                <div className='text-xs text-gray-500 dark:text-gray-400'>
                                  {booking.mobile}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className='py-4 px-6 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400'>
                            {new Date(booking.date).toLocaleDateString('en-IN')}
                          </td>

                          <td className='py-4 px-6 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300'>
                            {booking.time}
                          </td>

                          <td className='py-4 px-6 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400'>
                            <span className='inline-block px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-700 text-xs font-medium'>
                              {booking.paymentMode || "N/A"}
                            </span>
                          </td>

                          <td className='py-4 px-6 whitespace-nowrap'>
                            <select
                              value={booking.status || "Pending"}
                              onChange={(e) => requestStatusChange(booking, e.target.value)}
                              disabled={updatingId === booking._id}
                              className='rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 shadow-sm focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 transition-colors'
                            >
                              <option value="Pending">Pending</option>
                              <option value="Confirmed">Confirmed</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Completed">Completed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </td>

                          <td className='py-4 px-6 text-sm text-gray-600 dark:text-gray-300'>
                            {booking?.reviewSubmittedAt ? (
                              <div className='max-w-[220px]'>
                                <p className='flex items-center gap-1 text-amber-500 font-semibold'>
                                  <MdStar size={16} />
                                  {Number(booking?.reviewRating || 0).toFixed(1)} / 5
                                </p>
                                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2'>
                                  {booking?.reviewComment || "Review submitted"}
                                </p>
                              </div>
                            ) : (
                              <span className='inline-block rounded-full bg-gray-100 dark:bg-slate-700 px-2 py-1 text-xs'>
                                No review
                              </span>
                            )}
                          </td>

                          <td className='py-4 px-6 whitespace-nowrap text-right text-sm font-bold text-emerald-600 dark:text-emerald-400'>
                            ₹{Number(booking.amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={Boolean(pendingStatusChange)}
        onClose={closeStatusConfirmModal}
        title={pendingStatusChange?.status === "Confirmed" ? "Confirm Service Booking" : "Cancel Service Booking"}
        actions={[
          <button
            key='cancel'
            onClick={closeStatusConfirmModal}
            className='px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors'
          >
            Back
          </button>,
          <button
            key='confirm'
            onClick={confirmStatusChange}
            disabled={updatingId === pendingStatusChange?.booking?._id}
            className={`px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-60 ${
              pendingStatusChange?.status === "Confirmed"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {updatingId === pendingStatusChange?.booking?._id
              ? "Processing..."
              : pendingStatusChange?.status === "Confirmed"
                ? "Confirm Booking"
                : "Cancel Booking"}
          </button>,
        ]}
      >
        {pendingStatusChange?.booking && (
          <div className='space-y-3 text-sm'>
            <p className='text-gray-700'>Review full booking details before final action.</p>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <div className='rounded-lg border border-gray-200 p-3 bg-gray-50'>
                <p className='text-xs text-gray-500'>Service</p>
                <p className='font-semibold text-gray-900'>{pendingStatusChange.booking.serviceName || "N/A"}</p>
              </div>
              <div className='rounded-lg border border-gray-200 p-3 bg-gray-50'>
                <p className='text-xs text-gray-500'>Amount</p>
                <p className='font-semibold text-gray-900'>₹{Number(pendingStatusChange.booking.amount || 0).toFixed(2)}</p>
              </div>
              <div className='rounded-lg border border-gray-200 p-3 bg-gray-50'>
                <p className='text-xs text-gray-500'>Customer Name</p>
                <p className='font-semibold text-gray-900'>{pendingStatusChange.booking.name || "N/A"}</p>
              </div>
              <div className='rounded-lg border border-gray-200 p-3 bg-gray-50'>
                <p className='text-xs text-gray-500'>Customer Mobile</p>
                <p className='font-semibold text-gray-900'>{pendingStatusChange.booking.mobile || "N/A"}</p>
              </div>
              <div className='rounded-lg border border-gray-200 p-3 bg-gray-50'>
                <p className='text-xs text-gray-500'>Customer Email</p>
                <p className='font-semibold text-gray-900 break-all'>{pendingStatusChange.booking.email || "N/A"}</p>
              </div>
              <div className='rounded-lg border border-gray-200 p-3 bg-gray-50'>
                <p className='text-xs text-gray-500'>Slot</p>
                <p className='font-semibold text-gray-900'>
                  {pendingStatusChange.booking.date
                    ? new Date(pendingStatusChange.booking.date).toLocaleDateString("en-IN")
                    : "N/A"}
                  {" "}
                  {pendingStatusChange.booking.time || ""}
                </p>
              </div>
              <div className='rounded-lg border border-gray-200 p-3 bg-gray-50'>
                <p className='text-xs text-gray-500'>Assigned Employee</p>
                <p className='font-semibold text-gray-900'>{pendingStatusChange.booking.employeeName || "Auto assign"}</p>
              </div>
              <div className='rounded-lg border border-gray-200 p-3 bg-gray-50'>
                <p className='text-xs text-gray-500'>Payment Mode</p>
                <p className='font-semibold text-gray-900'>{pendingStatusChange.booking.paymentMode || "N/A"}</p>
              </div>
            </div>

            <div className={`rounded-lg p-3 border ${
              pendingStatusChange.status === "Confirmed"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}>
              {pendingStatusChange.status === "Confirmed"
                ? "On confirmation, customer will be notified and task will be assigned to employee immediately."
                : "On cancellation, customer will be notified immediately."}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}